import type { MapMouseEvent } from 'mapbox-gl';
import { filter, fromEvent, map, merge } from 'rxjs';
import { guards } from '@shared/helpers/typeguards.helpers';

export type PointDragModifiers = Pick<
  MouseEvent,
  'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'
>;
export type PointDragPointerMove = {
  event: MapMouseEvent;
  modifiers?: PointDragModifiers;
};

/** Refresh an active drag at the last pointer position when its modifiers change. */
export const createPointDragModifierMoves = (input: {
  target: EventTarget;
  getPointer: () => MapMouseEvent | undefined;
  isDragging: () => boolean;
}) =>
  merge(
    fromEvent<KeyboardEvent>(input.target, 'keydown'),
    fromEvent<KeyboardEvent>(input.target, 'keyup'),
  ).pipe(
    filter(
      (event) =>
        !event.repeat &&
        ['Shift', 'Control', 'Meta', 'Alt'].includes(event.key),
    ),
    filter(() => input.isDragging()),
    map((event): PointDragPointerMove | undefined => {
      const pointer = input.getPointer();
      if (!pointer) return undefined;
      return {
        event: pointer,
        modifiers: {
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          altKey: event.altKey,
        },
      };
    }),
    filter((event): event is PointDragPointerMove => guards.is.defined(event)),
  );
